import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { concatMap } from 'rxjs/operators';

import { VerificationDataRequirements } from 'ng-voxloud';
import { VerificationData, DID, DidByCompanyIdResponse } from 'voxloud-types/dist/models';

import { VerificationDataService, DidsService } from 'src/app/domains/dids/services';
import { OPTION_TYPE, VDFormValue } from 'src/app/shared/components';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';

@Component({
  selector: 'gdp-add-verification-data-popup',
  templateUrl: './add-verification-data-popup.component.html',
  styleUrls: ['./add-verification-data-popup.component.scss']
})
export class AddVerificationDataPopupComponent implements OnInit, OnDestroy {
  @Output() onCreateVerificationData: EventEmitter<DID> = new EventEmitter();
  public language: string;
  public did: DID;
  public companyId: number;
  public loading = {
    requirements: false,
    submit: false
  }
  public requirements: VerificationDataRequirements;
  public isVDRequired: boolean;
  public vdForm: VDFormValue = {
    type: '',
    isValid: false,
    value: null
  };
  public selectedOpt: { type: OPTION_TYPE, did: DID } = { type: OPTION_TYPE.NEW, did: null };
  public OPTION_TYPE = OPTION_TYPE;
  public didsWithVerificationData: DID[] = [];
  private languageSubscription: Subscription;

  constructor(
    private activeModal: NgbActiveModal,
    private verificationDataService: VerificationDataService,
    private didService: DidsService,
    private translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.language = this.translate.currentLang;
    this.languageSubscription = this.translate.onLangChange.subscribe((l: LangChangeEvent) => {
      this.language = l.lang;
    });
    this.getRequirements();
  }

  get canSubmit() {
    return this.vdForm.isValid;
  }

  getRequirements(): void {
    this.loading.requirements = true;
    forkJoin({
      r: this.verificationDataService.getVerificationDataRequirementsByCountry(this.did.country),
      d: this.didService.getDidsByCompanyId(this.companyId)
    }).subscribe((res: { r: VerificationDataRequirements, d: DidByCompanyIdResponse[] }) => {
      this.requirements = res.r;
      this.isVDRequired = !!(res.r.BUSINESS || res.r.PRIVATE);
      this.didsWithVerificationData = res.d[0].dids.filter((el: DID) => el.hasOwnProperty('verification_data_id'));
    },
      () => { this.close(); },
      () => { this.loading.requirements = false; }
    );
  }

  onFormChange(event): void {
    this.vdForm = {
      isValid: event.isValid,
      value: event.value,
      type: event.type
    };
    this.selectedOpt = event.selectedOpt;
  }

  onSubmit(): void {
    if (this.selectedOpt.type === OPTION_TYPE.CLONE) {
      this.loading.submit = true;
      this.verificationDataService.linkVerificationData(this.did.id, this.selectedOpt.did.verification_data_id)
        .subscribe(
          (did: DID) => {
            this.loading.submit = false;
            this.onCreateVerificationData.emit(did);
            this.close();
          }, () => {
            this.loading.submit = false;
            this.close();
          });
    }

    if (this.vdForm.isValid) {
      let payload = this.verificationDataService.buildVerificationData(
        this.companyId,
        this.did.country,
        {
          type: this.vdForm.type,
          data: this.requirements,
          value: this.vdForm
        }
      );
      this.loading.submit = true;
      let verificationDataId: number;
      this.verificationDataService.createVerificationData(payload).pipe(
        concatMap((res: VerificationData) => {
          verificationDataId = res.id;
          return this.didService.getDid(this.did.number)
        }),
        concatMap((res: DID) => {
          return this.verificationDataService.linkVerificationData(res.id, verificationDataId)
        })
      ).subscribe((did: DID) => {
        this.loading.submit = false;
        this.onCreateVerificationData.emit(did);
        this.close();
      }, () => {
        this.loading.submit = false;
        this.close();
      });
    }

  }

  close() {
    this.activeModal.close();
  }

  ngOnDestroy() {
    if (this.languageSubscription) this.languageSubscription.unsubscribe();
  }

}
